import { createTheme } from '@mui/material';
import { deepmerge } from '@mui/utils';
import baseThemeColors from 'src/theme/colors';
import dialogTheme from 'src/theme/dialog';
import customTypography from 'src/theme/typographies';
import { PlatformPortal } from './types';

export const DEFAULT_PORTAL_FONT_FAMILY = 'Poppins';

export type PortalThemeTokens = {
  fontFamily: string;
  fontCssUrl: string | null;
  colors: {
    primary: string;
    primaryDark: string;
    primaryLight: string;
    primaryLighter: string;
    textPrimary: string;
    textSecondary: string;
    background: string;
  };
};

const defaultTokens: PortalThemeTokens = {
  fontFamily: DEFAULT_PORTAL_FONT_FAMILY,
  fontCssUrl: null,
  colors: {
    primary: baseThemeColors.ghgMain,
    primaryDark: baseThemeColors.ghgMainDark,
    primaryLight: baseThemeColors.ghgMainLight,
    primaryLighter: baseThemeColors.ghgMainLighter,
    textPrimary: baseThemeColors.ghgDark,
    textSecondary: baseThemeColors.ghgDarkGrey,
    background: baseThemeColors.background,
  },
};

const colorKeys = Object.keys(defaultTokens.colors) as Array<
  keyof PortalThemeTokens['colors']
>;

export function isValidCssColor(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) return false;

  if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
    return CSS.supports('color', value);
  }

  return (
    /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value) ||
    /^rgba?\([^)]+\)$/i.test(value) ||
    /^hsla?\([^)]+\)$/i.test(value)
  );
}

function getStringToken(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function getFontCssUrl(value: unknown): string | null {
  const url = getStringToken(value);
  if (!url) return null;

  if (url.startsWith('/')) return url;

  try {
    const parsedUrl = new URL(url);
    if (['http:', 'https:'].includes(parsedUrl.protocol)) return url;
  } catch {
    return null;
  }

  return null;
}

export function getPortalThemeTokens(
  portal?: PlatformPortal | null
): PortalThemeTokens {
  const themeConfig = portal?.theme_config || {};
  const configColors =
    themeConfig.colors && typeof themeConfig.colors === 'object'
      ? themeConfig.colors
      : {};
  const colors = { ...defaultTokens.colors };

  if (isValidCssColor(themeConfig.primaryColor)) {
    colors.primary = themeConfig.primaryColor;
  }

  colorKeys.forEach((key) => {
    const value = configColors[key];
    if (isValidCssColor(value)) colors[key] = value;
  });

  return {
    fontFamily:
      getStringToken(themeConfig.fontFamily) || defaultTokens.fontFamily,
    fontCssUrl: getFontCssUrl(themeConfig.fontCssUrl),
    colors,
  };
}

export function createAppTheme(portal?: PlatformPortal | null) {
  const tokens = getPortalThemeTokens(portal);

  const baseTheme = createTheme({
    typography: {
      fontFamily: tokens.fontFamily,
      ...customTypography,
    },
    palette: {
      primary: {
        main: tokens.colors.primary,
        contrastText: baseThemeColors.pureWhite,
      },
      secondary: {
        main: baseThemeColors.pureWhite,
        contrastText: tokens.colors.primary,
      },
      error: {
        main: baseThemeColors.error,
      },
      hover: baseThemeColors.hover,
      pressed: baseThemeColors.pressed,
      disabledLabel: baseThemeColors.disabledLabel,
      disabledGraphics: baseThemeColors.disabledGraphics,
      placeholder: baseThemeColors.placeholder,
      strokeInputs: baseThemeColors.strokeInputs,
      backgroundInput: baseThemeColors.backgroundInput,
      background: {
        default: tokens.colors.background,
      },
      dark: baseThemeColors.dark,
      darkGrey: baseThemeColors.darkGrey,
      footerBackground: baseThemeColors.footer,
      footerText: tokens.colors.textSecondary,
    },
    components: {
      MuiButton: {
        defaultProps: {
          variant: 'primary',
          size: 'medium',
        },
        styleOverrides: {
          root: {
            fontWeight: 600,
            boxShadow: 'none',
            borderRadius: 28,
            fontSize: 18,
            height: 56,
            padding: '0 24px',
            lineHeight: 1.25,
            minWidth: '144px',

            ':hover': {
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: baseThemeColors.pressed,
              color: baseThemeColors.disabledLabel,
            },
          },
          // @ts-ignore
          neutral: {
            minWidth: '120px',
            marginRight: '5px',
            backgroundColor: '#fff',
            color: '#606462',
            fontSize: '18px',
            borderRadius: '16px',
            textTransform: 'none',
            '& svg': {
              fontSize: '24px',
            },
            '&:hover': {
              backgroundColor: '#f2f2f2',
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: ({ theme }) =>
            theme.unstable_sx({
              fontFamily: tokens.fontFamily,
              '& a': {
                color: theme.palette.primary.main,
              },
            }),
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            '& .MuiCheckbox-root': {
              flexShrink: 0,
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: '24px',
            '&:hover': {
              backgroundColor: tokens.colors.primaryLight,
            },
            '&.active': {
              backgroundColor: baseThemeColors.pureWhite,
              color: tokens.colors.textPrimary,
            },
          },
        },
      },
      MuiBox: {
        variants: [
          {
            props: { variant: 'footer' },
            style: {
              backgroundColor: '#1976d2',
              color: '#fff',
              padding: '20px',
              textAlign: 'center',
            },
          },
        ],
      },
    },
  });

  return createTheme(deepmerge(baseTheme, dialogTheme));
}

export function createGhgTheme(portal?: PlatformPortal | null) {
  const tokens = getPortalThemeTokens(portal);

  return createTheme(
    deepmerge(createAppTheme(portal), {
      palette: {
        text: {
          primary: tokens.colors.textPrimary,
          secondary: tokens.colors.textSecondary,
        },
        main: tokens.colors.primary,
        mainDark: tokens.colors.primaryDark,
        mainLight: tokens.colors.primaryLight,
        mainLighter: tokens.colors.primaryLighter,
      },
      components: {
        MuiDialog: {
          styleOverrides: {
            root: {
              backdropFilter: 'blur(2px) sepia(5%)',
              '& .MuiPaper-root': {
                borderRadius: '16px',
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              padding: '0 18px',
              textTransform: 'none',
              ':hover': {
                backgroundColor: tokens.colors.primaryLight,
              },
              img: {
                width: '20px',
                height: '20px',
              },
            },
            primary: {
              fontWeight: 700,
              backgroundColor: tokens.colors.primary,
              color: baseThemeColors.pureWhite,
              '&:hover': {
                backgroundColor: tokens.colors.primaryLight,
              },
              '&:pressed': {
                backgroundColor: tokens.colors.primaryLighter,
              },
            },
            secondary: {
              color: tokens.colors.primary,
              border: `1px solid ${baseThemeColors.disabledGraphics}`,
              backgroundColor: baseThemeColors.pureWhite,
              '&:hover': {
                backgroundColor: baseThemeColors.hover,
              },
              '&:pressed': {
                backgroundColor: baseThemeColors.pressed,
              },
              '&:inactive': {
                backgroundColor: baseThemeColors.disabledGraphics,
                color: baseThemeColors.disabledLabel,
              },
            },
            sizeSmall: {
              height: '32px',
              fontSize: '13px',
            },
            sizeMedium: {
              height: '40px',
              fontSize: '15px',
            },
            sizeLarge: {
              height: '48px',
              fontSize: '16px',
            },
            textPrimary: {
              color: tokens.colors.primary,
              ':hover': {
                color: baseThemeColors.pureWhite,
              },
            },
            textSecondary: {
              color: tokens.colors.textPrimary,
              border: `1px solid ${baseThemeColors.strokeInputs}`,
              ':hover': {
                color: baseThemeColors.pureWhite,
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                '&.Black': {
                  borderColor: 'black',
                },
              },
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '18px',
              lineHeight: '28px',
              letterSpacing: '0.54px',
            },
          },
        },
        MuiListItem: {
          styleOverrides: {
            root: {
              padding: 0,
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              borderRadius: '24px',
              '&:hover': {
                backgroundColor: tokens.colors.primaryLight,
              },
              '&.active': {
                backgroundColor: baseThemeColors.pureWhite,
                color: tokens.colors.textPrimary,
              },
            },
          },
        },
      },
    })
  );
}
